<template>
  <v-card flat class="py-4 px-6">
    <v-card-title>Cotizaciones por status de oportunidad</v-card-title>
    <apexchart
      type="bar"
      :height="300"
      :options="chartOptions"
      :series="series"
    />
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps<{
  data: Record<string, any>
}>();

// === Dark mode (sólo para tooltip/labels) ===
const isDarkMode = ref(window.matchMedia('(prefers-color-scheme: dark)').matches);
window.matchMedia('(prefers-color-scheme: dark)')
  .addEventListener('change', e => isDarkMode.value = e.matches);

// === Datos base ===
const categories = computed(() => Object.keys(props.data || {}));

const series = computed(() => [
  {
    name: 'Total',
    data: Object.values(props.data || {}).map((item: any) => Number(item.total_quotations ?? 0)),
  }
]);

// === Paletas dinámicas por hostname ===
const hostname = window.location.hostname;

const dominiomediosPalette = [
  '#be103b', '#88102e', '#6e0f26', '#a1293a',
  '#944249', '#b5696d', '#be103b'
];

// Escala azul basada en #2563eb (Tailwindish)
const blueScaleBase = [
  '#1e3a8a', // blue-900
  '#1d4ed8', // blue-700
  '#2563eb', // blue-600 (tu color pedido)
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#bfdbfe'  // blue-200
];

const computedColors = computed(() => {
  const n = categories.value.length || 7;

  // Host específico
  if (hostname === 'crm.dominiomedios.com') {
    // recorta o cicla si hay más categorías que colores
    if (n <= dominiomediosPalette.length) return dominiomediosPalette.slice(0, n);
    return Array.from({ length: n }, (_, i) => dominiomediosPalette[i % dominiomediosPalette.length]);
  }

  // Default: escala azul
  if (n <= blueScaleBase.length) return blueScaleBase.slice(0, n);
  return Array.from({ length: n }, (_, i) => blueScaleBase[i % blueScaleBase.length]);
});

// === Opciones del gráfico ===
const chartOptions = computed(() => ({
  chart: { type: 'bar' },
  plotOptions: {
    bar: {
      horizontal: true,
      dataLabels: { position: 'top' }
    }
  },
  dataLabels: {
    offsetX: -15,
    offsetY: 1,
    enabled: true,
    formatter: (val: number) => val,
    style: { colors: ['#fff'] }
  },
  xaxis: {
    categories: categories.value,
    labels: { show: false }
  },
  yaxis: {
    labels: { style: { colors: isDarkMode.value ? '#fff' : '#333' } }
  },
  grid: { show: false },
  colors: computedColors.value, // ← aquí usamos la paleta dinámica
  tooltip: {
    theme: isDarkMode.value ? 'dark' : 'light',
    y: {
      formatter: (val: number, { dataPointIndex }) => {
        const etapas = Object.keys(props.data || {});
        const key = etapas[dataPointIndex];
        const sumTotal = (props.data?.[key]?.sum_total ?? 0);
        return parseFloat(sumTotal).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
      }
    }
  }
}));
</script>

<script lang="ts">
export default { name: 'ProspectsByStatus' }
</script>
